import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const PageNotFound = () => {
  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-zinc-50 px-6 text-zinc-950">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-50 via-zinc-100 to-slate-200" />

      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(24,24,27,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(24,24,27,0.06) 1px, transparent 1px)",
          backgroundSize: "42px 42px",
        }}
      />

      {/* Animated background shapes */}
      <motion.div
        aria-hidden="true"
        className="absolute -left-24 top-16 h-80 w-80 rounded-full bg-indigo-300/30 blur-3xl"
        animate={{
          x: [0, 70, 20, 0],
          y: [0, 40, 100, 0],
          scale: [1, 1.15, 0.95, 1],
        }}
        transition={{
          duration: 16,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        aria-hidden="true"
        className="absolute -right-24 bottom-10 h-96 w-96 rounded-full bg-sky-300/30 blur-3xl"
        animate={{
          x: [0, -60, -20, 0],
          y: [0, -70, -20, 0],
          scale: [1, 0.9, 1.1, 1],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Main card */}
      <motion.section
        initial={{ opacity: 0, y: 30, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: 0.7,
          ease: "easeOut",
        }}
        className="relative z-10 w-full max-w-3xl text-center"
      >
        <motion.div
          animate={{
            y: [0, -10, 0],
            rotate: [0, 1, -1, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-3xl border border-white/70 bg-white/80 shadow-xl shadow-zinc-400/20 backdrop-blur-xl"
        >
          <svg
            width="38"
            height="38"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            className="text-zinc-800"
          >
            <path
              d="M9.5 9.5H9.51M14.5 9.5H14.51M9 16C10 15 11 14.5 12 14.5C13 14.5 14 15 15 16"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="text-sm font-semibold uppercase tracking-[0.3em] text-indigo-600"
        >
          Error 404
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.2,
            duration: 0.6,
          }}
          className="mt-5 text-7xl font-semibold tracking-tighter text-zinc-950 sm:text-8xl lg:text-9xl"
        >
          Lost in the code.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.3,
            duration: 0.6,
          }}
          className="mx-auto mt-7 max-w-xl text-lg leading-8 text-zinc-600 sm:text-xl"
        >
          The page you are looking for does not exist, may have moved, or was
          never deployed in the first place.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.4,
            duration: 0.6,
          }}
          className="mt-10 flex flex-wrap justify-center gap-4"
        >
          <Link
            to="/"
            className="group inline-flex items-center gap-2 rounded-xl bg-zinc-950 px-6 py-3.5 font-semibold text-white shadow-lg shadow-zinc-950/10 transition duration-200 hover:-translate-y-0.5 hover:bg-zinc-800"
          >
            Back to home

            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              className="transition-transform duration-200 group-hover:-translate-x-1"
            >
              <path
                d="M19 12H5M5 12L11 6M5 12L11 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>

          <Link
            to="/projects/home"
            className="inline-flex items-center rounded-xl border border-zinc-300 bg-white/80 px-6 py-3.5 font-semibold text-zinc-900 shadow-sm backdrop-blur-md transition duration-200 hover:-translate-y-0.5 hover:border-zinc-400 hover:bg-white"
          >
            View projects
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mx-auto mt-14 max-w-lg overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-950 text-left shadow-xl"
        >
          <div className="flex items-center gap-2 border-b border-zinc-800 px-5 py-4">
            <span className="h-3 w-3 rounded-full bg-red-400" />
            <span className="h-3 w-3 rounded-full bg-amber-400" />
            <span className="h-3 w-3 rounded-full bg-emerald-400" />

            <span className="ml-3 font-mono text-xs text-zinc-500">
              router.js
            </span>
          </div>

          <div className="p-6 font-mono text-sm leading-7">
            <p>
              <span className="text-violet-400">const</span>{" "}
              <span className="text-sky-300">page</span>{" "}
              <span className="text-zinc-500">=</span>{" "}
              <span className="text-emerald-300">undefined</span>
              <span className="text-zinc-300">;</span>
            </p>

            <p className="mt-2">
              <span className="text-violet-400">return</span>{" "}
              <span className="text-emerald-300">"Let’s get you home"</span>
              <span className="text-zinc-300">;</span>
            </p>
          </div>
        </motion.div>
      </motion.section>
    </main>
  );
};

export default PageNotFound;

